/**
 * @file: useTrackingTransparency.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
import { useCurrentUser } from '@euler/app/flows/auth/context';
import { useServiceFactory } from '@euler/services/factory';
import { makeDebug } from '@euler/utils';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  PermissionStatus,
  useTrackingPermissions,
} from 'expo-tracking-transparency';
import { useEffect, useRef } from 'react';

const debug = makeDebug('app-tracking-transparency');

export const useTrackingTransparency = () => {
  const [status, requestPermission] = useTrackingPermissions();
  const uid = useCurrentUser()?.user.uid;
  const requested = useRef(false);
  const { userService } = useServiceFactory();

  useEffect(() => {
    debug('status: %O', status);

    const updateTrackingSetting = async (value: PermissionStatus) => {
      if (uid == null) {
        debug('update tracking setting, skipped');
        return;
      }

      const key = `${uid}:app-tracking-transparency-setting`;

      try {
        const cachedValue = await AsyncStorage.getItem(key);
        if (cachedValue === value) {
          debug('tracking transparency setting not changed, update skipped');
          return;
        }
      } catch (e) {
        console.error(
          'error get cached app tracking transparency setting: ',
          e,
        );
      }

      try {
        await userService.updateIosTrackingTransparencySetting(value);
        debug('user tracking transparency settings update successful');

        await AsyncStorage.setItem(key, value);
        debug('user tracking transparency setting "%s" cached', value);
      } catch (e) {
        console.error('user tracking transparency settings update failed: ', e);
      }
    };

    if (status == null) {
      return;
    }

    if (status.status !== PermissionStatus.UNDETERMINED || requested.current) {
      void updateTrackingSetting(status.status);
      return;
    }

    requested.current = true;

    debug('requesting permission... ');
    requestPermission()
      .then(r => updateTrackingSetting(r.status))
      .catch(err => {
        console.error('error request tracking transparency permission: ', err);
      });
  }, [requestPermission, status, uid, userService]);
};
